// AdminBusinesses.js — /admin/businesses list + edit modal.
//
// UPSTREAM API calls (all auth-gated via authGet/authPut/authDelete cookie):
//   - GET    /api/admin/businesses?paginated=true&include_btcmap=...
//   - PUT    /api/admin/businesses/:id
//   - DELETE /api/admin/businesses/:id
//   (See backend/routes/businesses.py :: get_all_businesses / update_business.)
//
// DOWNSTREAM behaviour this page triggers server-side:
//   - PUT that transitions `approved: false → true` fires the OSM submit
//     pipeline via background_tasks in backend (_osm_submit_and_save).
//   - DELETE cleans up the record; if osm_node_id exists the backend also
//     issues an OSM node delete (services/osm_submit.delete_osm_node).
//
// Cascading pickers (Based Location + Service Area) implement the same
// scope hierarchy the backend filters on:
//   global → region → country → state → county
// `migrateLegacyFields` upgrades pre-scope records on modal open;
// `deriveServiceFields` clears deeper-scope fields on save to avoid
// internally inconsistent records (e.g. scope='country' with a state set).
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authGet, authPut, authPost, authDelete, getAuthConfig } from '../../utils/auth';
import { Pencil, Trash2, Download, Check, X, Copy, ExternalLink, ArrowLeft, CheckCircle, XCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import COUNTRIES from '@/data/countries.json';
import REGIONS_DATA from '@/data/regions.json';
import COUNTRY_NAMES from '@/data/countryNames';
import { DEFAULT_CATEGORIES } from '@/data/adminDefaults';

// ── ABIB membership tiers ─────────────────────────────────────────────────────
// Mirror of backend _MEMBER_CATEGORY_LABELS / ABIB_MEMBERSHIP_TIERS. Only tiered
// members appear in the ABIB Member Spotlight social posts.
const ABIB_TIER_OPTIONS = [
  { value: 'orange_label_sponsor', label: 'Orange Label Sponsor', short: 'Sponsor' },
  { value: 'business_chamber', label: 'Business Chamber', short: 'Chamber' },
  { value: 'industry_member', label: 'Industry Member', short: 'Industry' },
];
const ABIB_TIER_SHORT = Object.fromEntries(ABIB_TIER_OPTIONS.map(t => [t.value, t.short]));

// ── Cascading picker helpers ──────────────────────────────────────────────────

const UN_REGIONS = [
  { slug: 'oceania',  label: 'Oceania' },
  { slug: 'asia',     label: 'Asia' },
  { slug: 'americas', label: 'Americas' },
  { slug: 'europe',   label: 'Europe' },
  { slug: 'africa',   label: 'Africa' },
];

/** Compact relative date for the Added column. Falls back to '—' on bad input. */
const formatAddedAgo = (iso) => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  const diffYr = Math.round(diffMo / 12);
  return `${diffYr}y ago`;
};

/** Full timestamp for tooltip on the Added column. */
const formatAddedFull = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

/** Return sorted [{code, name}] list for all ISO2 codes, using countryNames. */
const ALL_COUNTRY_OPTIONS = Object.keys(COUNTRIES)
  .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
  .sort((a, b) => a.name.localeCompare(b.name));

/** Return [{code, name}] list filtered to a UN region slug. */
const getCountriesForRegion = (regionSlug) => {
  if (!regionSlug) return ALL_COUNTRY_OPTIONS;
  const regionData = REGIONS_DATA[regionSlug];
  if (!regionData) return ALL_COUNTRY_OPTIONS;
  const allowed = new Set(regionData.countries || []);
  return ALL_COUNTRY_OPTIONS.filter(c => allowed.has(c.code));
};

/**
 * Upgrade legacy AU state abbreviation to ISO 3166-2 if country is AU.
 * e.g. "NSW" → "AU-NSW", "Victoria" → "AU-VIC"
 */
const _AU_ABBR_MAP = {
  NSW: 'AU-NSW', VIC: 'AU-VIC', VICTORIA: 'AU-VIC',
  QLD: 'AU-QLD', QUEENSLAND: 'AU-QLD',
  SA: 'AU-SA', 'SOUTH AUSTRALIA': 'AU-SA',
  WA: 'AU-WA', 'WESTERN AUSTRALIA': 'AU-WA',
  TAS: 'AU-TAS', TASMANIA: 'AU-TAS',
  NT: 'AU-NT', 'NORTHERN TERRITORY': 'AU-NT',
  ACT: 'AU-ACT',
};
const upgradeAuState = (val) => {
  if (!val) return val;
  return _AU_ABBR_MAP[val.toUpperCase()] || val;
};

/** UN region slug names for lookup */
const UN_SLUG_SET = new Set(UN_REGIONS.map(r => r.slug));

/**
 * Migrate legacy region / based_state fields into new canonical fields.
 * Called once when opening the edit dialog. Mutates a copy of editData.
 */
const migrateLegacyFields = (data) => {
  const d = { ...data };

  // ── Based state: upgrade bare AU abbreviation if country is AU
  if (d.country_code === 'AU' && d.based_state && !d.based_state.startsWith('AU-')) {
    d.based_state = upgradeAuState(d.based_state) || d.based_state;
  }

  // ── Service scope: infer if missing
  if (!d.service_scope) {
    const regionRaw = (d.region || '').toLowerCase().trim();
    if (regionRaw === 'global') {
      d.service_scope = 'global';
    } else if (UN_SLUG_SET.has(regionRaw)) {
      d.service_scope = 'region';
      d.service_region = d.service_region || regionRaw;
    } else if (d.country_code) {
      d.service_scope = 'country';
      d.service_country = d.service_country || d.country_code;
    }
    // else leave empty — user must set explicitly
  }

  return d;
};

/**
 * Before saving, clear fields that are deeper than the current scope
 * to keep the record internally consistent.
 *
 * NOTE: we send '' (empty string), NOT null, for cleared fields. The backend
 * update endpoint drops null values (`if v is not None`), so a null would be a
 * silent no-op and the stale value would persist. '' actually clears the field.
 */
const deriveServiceFields = (data) => {
  const d = { ...data };
  const scope = d.service_scope || '';
  if (scope === 'global') {
    d.service_region = '';
    d.service_country = '';
    d.service_state = '';
    d.service_county = '';
  } else if (scope === 'region') {
    d.service_country = '';
    d.service_state = '';
    d.service_county = '';
  } else if (scope === 'country') {
    d.service_state = '';
    d.service_county = '';
  } else if (scope === 'state') {
    d.service_county = '';
  }
  // scope === 'county' — keep all
  return d;
};

const REGION_OPTIONS = [
  { slug: 'all', label: 'All regions' },
  { slug: 'global', label: 'Global' },
  { slug: 'oceania', label: 'Oceania' },
  { slug: 'asia', label: 'Asia' },
  { slug: 'americas', label: 'Americas' },
  { slug: 'europe', label: 'Europe' },
  { slug: 'africa', label: 'Africa' },
];

const getBusinessRegion = (b) => {
  // Legacy records written before `service_scope` existed carry the global
  // marker on `service_area` or a literal `region: "global"`. Treat any of
  // those as global so the admin Global region chip surfaces them.
  if (
    b.service_scope === 'global' ||
    b.service_area === 'global' ||
    (b.region || '').toLowerCase() === 'global'
  ) return 'global';
  const code = (b.country_code || '').toUpperCase();
  return COUNTRIES[code]?.region || b.service_region || null;
};


const API = '/api';

// Component for granular field-by-field suggestion approval
const SuggestionCard = ({ suggestion, onApprove, onReject }) => {
  const isFlag = suggestion.kind === 'flag' ||
    (!suggestion.kind && !Object.keys(suggestion.suggested_changes || {}).length);
  const [selectedFields, setSelectedFields] = useState(
    Object.keys(suggestion.suggested_changes || {})
  );
  const [expanded, setExpanded] = useState(isFlag); // flags show reason immediately

  const fields = Object.entries(suggestion.suggested_changes || {});
  const hasMultipleFields = fields.length > 1;
  
  const toggleField = (field) => {
    setSelectedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };
  
  const toggleAll = () => {
    if (selectedFields.length === fields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(fields.map(([f]) => f));
    }
  };
  
  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (Array.isArray(value)) return value.join(', ') || '(empty)';
    return String(value);
  };

  return (
    <div className={`bg-card rounded-lg border overflow-hidden ${isFlag ? 'border-red-200 dark:border-red-800' : 'border-amber-200 dark:border-amber-800'}`}>
      {/* Header */}
      <div
        className={`p-3 cursor-pointer transition-colors ${isFlag
          ? 'bg-gradient-to-r from-red-50 dark:from-red-900/20 to-card hover:bg-red-100/50'
          : 'bg-gradient-to-r from-amber-50 dark:from-amber-900/20 to-card hover:bg-amber-100/50'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{suggestion.business_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isFlag
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                {isFlag ? 'Report' : `${fields.length} change${fields.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            {suggestion.submitter_name && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Submitted by: {suggestion.submitter_name}
                {suggestion.submitter_email && ` (${suggestion.submitter_email})`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground mr-2">
              {expanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            <svg
              className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && isFlag && (
        <div className="border-t border-red-100 dark:border-red-900/30 p-3 space-y-3">
          <p className="text-sm whitespace-pre-wrap">
            <span className="font-medium">Reason: </span>
            {suggestion.reason || '(no reason provided)'}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReject(suggestion.id)}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={() => onApprove(suggestion.id, [])}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Mark actioned
            </Button>
          </div>
        </div>
      )}
      {expanded && !isFlag && (
        <div className="border-t border-amber-100">
          {/* Field selection */}
          <div className="p-3 space-y-2">
            {hasMultipleFields && (
              <div className="flex items-center justify-between pb-2 border-b border-border">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-muted-foreground">
                  <Checkbox
                    checked={selectedFields.length === fields.length}
                    onCheckedChange={toggleAll}
                  />
                  Select All Fields
                </label>
                <span className="text-xs text-muted-foreground">
                  {selectedFields.length} of {fields.length} selected
                </span>
              </div>
            )}
            
            {fields.map(([field, change]) => (
              <div 
                key={field} 
                className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                  selectedFields.includes(field) ? 'bg-green-50 dark:bg-green-900/20' : 'bg-muted'
                }`}
              >
                <Checkbox
                  checked={selectedFields.includes(field)}
                  onCheckedChange={() => toggleField(field)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground">{formatFieldName(field)}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <span className="text-red-600 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded line-through max-w-[200px] truncate" title={formatValue(change.old)}>
                      {formatValue(change.old)}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-green-600 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded max-w-[200px] truncate" title={formatValue(change.new)}>
                      {formatValue(change.new)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center justify-between gap-2 p-3 bg-muted border-t border-border">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReject(suggestion.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject All
            </Button>
            <div className="flex gap-2">
              {hasMultipleFields && selectedFields.length > 0 && selectedFields.length < fields.length && (
                <Button
                  size="sm"
                  onClick={() => onApprove(suggestion.id, selectedFields)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Apply Selected ({selectedFields.length})
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => onApprove(suggestion.id, fields.map(([f]) => f))}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={selectedFields.length === 0}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {hasMultipleFields ? 'Apply All' : 'Apply Change'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Collapsed quick actions */}
      {!expanded && (
        <div className="flex items-center justify-end gap-2 px-3 pb-3">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onReject(suggestion.id); }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
          >
            <XCircle className="w-3 h-3 mr-1" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onApprove(suggestion.id, fields.map(([f]) => f)); }}
            className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Accept All
          </Button>
        </div>
      )}
    </div>
  );
};

const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null); // business pending approval
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortField, setSortField] = useState('business_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [inlineEdit, setInlineEdit] = useState({ id: null, field: null, value: '' });
  // Tap-to-toggle divider state: controls which of the two groups is visible.
  // Approved is the default view; tapping a divider collapses the other group.
  const [showPending, setShowPending] = useState(true);
  const [showApproved, setShowApproved] = useState(true);
  const [regionFilter, setRegionFilter] = useState('all');
  // Country-states cache: { [iso2]: [{code, name}] | [] }
  const countryStatesCache = useRef({});
  // States for Based Location picker
  const [basedCountryStates, setBasedCountryStates] = useState([]);
  // States for Service Area country-state picker
  const [serviceCountryStates, setServiceCountryStates] = useState([]);
  const navigate = useNavigate();

  /** Fetch country states (cached). Returns [{code, name}] or []. */
  const fetchCountryStates = async (iso2) => {
    if (!iso2) return [];
    const key = iso2.toUpperCase();
    if (countryStatesCache.current[key] !== undefined) return countryStatesCache.current[key];
    try {
      const res = await axios.get(`${API}/public/country-states/${key}`);
      const states = res.data?.states || [];
      countryStatesCache.current[key] = states;
      return states;
    } catch {
      countryStatesCache.current[key] = [];
      return [];
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await authGet('/admin/suggestions');
      setSuggestions(response.data?.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleApproveSuggestion = async (suggestionId, approvedFields) => {
    try {
      await authPost(`/admin/suggestions/${suggestionId}/approve`, { approved_fields: approvedFields });
      toast.success('Changes applied');
      fetchSuggestions();
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to approve suggestion');
    }
  };

  const handleRejectSuggestion = async (suggestionId) => {
    try {
      await authPost(`/admin/suggestions/${suggestionId}/reject`);
      toast.success('Suggestion rejected');
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to reject suggestion');
    }
  };
  
  // Page size for the admin list. The backend returns a {items, total,
  // next_offset} envelope; we track total + next_offset so records beyond the
  // first page stay visible (and editable) via "Load more".
  const PAGE_SIZE = 500;

  const fetchBusinesses = async ({ offset = 0, append = false } = {}) => {
    try {
      if (append) setLoadingMore(true);
      const response = await authGet(`/admin/businesses?paginated=true&limit=${PAGE_SIZE}&offset=${offset}`);
      const payload = response.data;
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      setBusinesses(prev => (append ? [...prev, ...items] : items));
      if (Array.isArray(payload)) {
        // Legacy non-paginated response (whole list in one shot).
        setTotal(items.length);
        setNextOffset(null);
      } else {
        setTotal(payload?.total ?? items.length);
        setNextOffset(payload?.next_offset ?? null);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
      setLoading(false);
    } finally {
      if (append) setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (nextOffset != null) fetchBusinesses({ offset: nextOffset, append: true });
  };

  /**
   * Pull every page in one go. The region filter is applied client-side over
   * `businesses`, so with server pagination it would only ever see the loaded
   * subset (and the footer total would mislead). When a region filter is
   * engaged we load the whole result set so the filter and count are accurate.
   */
  const fetchAllBusinesses = async () => {
    try {
      setLoadingMore(true);
      let all = [];
      let offset = 0;
      let tot = 0;
      for (;;) {
        const response = await authGet(`/admin/businesses?paginated=true&limit=${PAGE_SIZE}&offset=${offset}`);
        const payload = response.data;
        const items = Array.isArray(payload) ? payload : (payload?.items || []);
        all = all.concat(items);
        if (Array.isArray(payload)) { tot = all.length; break; }
        tot = payload?.total ?? all.length;
        const no = payload?.next_offset ?? null;
        if (no == null) break;
        offset = no;
      }
      setBusinesses(all);
      setTotal(tot);
      setNextOffset(null);
    } catch (error) {
      console.error('Error fetching all businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setLoadingMore(false);
    }
  };

  // Region chips filter client-side; ensure the full set is loaded first so the
  // filter isn't limited to the currently-loaded page(s).
  const handleRegionFilter = (slug) => {
    setRegionFilter(slug);
    if (slug !== 'all' && nextOffset != null) fetchAllBusinesses();
  };

  /**
   * Single funnel for every admin business mutation: auth-cookie PUT +
   * toast + list refresh. Returns true on success so callers can clear
   * their local edit state only when the write actually landed.
   */
  const updateBusiness = async (id, patch, { successMsg = 'Updated successfully', errorMsg = 'Failed to update' } = {}) => {
    try {
      const response = await authPut(`/admin/businesses/${id}`, patch);
      if (successMsg) toast.success(successMsg);
      // Patch the single affected record in place so any pages loaded via
      // "Load more" (and the just-edited row) stay visible. The PUT returns the
      // canonical updated record; fall back to a local merge if it's missing.
      const updated = response?.data;
      setBusinesses(prev => prev.map(b => (
        b.id === id ? (updated && updated.id ? updated : { ...b, ...patch }) : b
      )));
      return true;
    } catch (error) {
      console.error('Error updating business:', error);
      toast.error(errorMsg);
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        if (!response.data || response.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }
        fetchBusinesses();
      } catch (error) {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]); // eslint-disable-line

  useEffect(() => {
    fetchSuggestions();
  }, []); // eslint-disable-line
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedBusinesses = useMemo(() => {
    // Pending (approved=false) always pinned to the top; within each section
    // apply the current column sort.
    const bySortField = (a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'category') {
        aVal = (a.category || []).join(', ');
        bVal = (b.category || []).join(', ');
      }
      if (sortField === 'approved') {
        aVal = a.approved ? 1 : 0;
        bVal = b.approved ? 1 : 0;
      }
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        aVal = String(aVal ?? '').toLowerCase();
        bVal = String(bVal ?? '').toLowerCase();
      }
      let cmp = 0;
      if (aVal < bVal) cmp = -1;
      else if (aVal > bVal) cmp = 1;
      return sortDirection === 'asc' ? cmp : -cmp;
    };
    const scoped = regionFilter === 'all'
      ? businesses
      : businesses.filter(b => getBusinessRegion(b) === regionFilter);
    const pending = scoped.filter(b => !b.approved).sort(bySortField);
    const approved = scoped.filter(b => b.approved).sort(bySortField);
    return [...pending, ...approved];
  }, [businesses, regionFilter, sortField, sortDirection]);
  
  const handleEdit = async (business) => {
    setSelectedBusiness(business);
    const migrated = migrateLegacyFields(business);
    setEditData(migrated);
    setEditDialog(true);
    // Pre-fetch states for both Based Location and Service Area country pickers
    if (migrated.country_code) {
      const states = await fetchCountryStates(migrated.country_code);
      setBasedCountryStates(states);
    } else {
      setBasedCountryStates([]);
    }
    if (migrated.service_country) {
      const states = await fetchCountryStates(migrated.service_country);
      setServiceCountryStates(states);
    } else {
      setServiceCountryStates([]);
    }
  };
  
  const handleInlineEdit = (id, field, value) => {
    setInlineEdit({ id, field, value });
  };
  
  const handleInlineCancel = () => {
    setInlineEdit({ id: null, field: null, value: '' });
  };

  const handleInlineCategorySave = async () => {
    if (!inlineEdit.id || !inlineEdit.value) return;
    // For category, we set the entire array to just the selected category
    const ok = await updateBusiness(inlineEdit.id, { category: [inlineEdit.value] }, {
      successMsg: 'Category updated successfully',
      errorMsg: 'Failed to update category',
    });
    if (ok) setInlineEdit({ id: null, field: null, value: '' });
  };

  const handleToggleApproved = (business) => {
    // false → true fires the irreversible OSM node-create pipeline server-side,
    // so gate that transition behind a confirm. Pulling back to pending is safe.
    if (!business.approved) {
      setApproveTarget(business);
      setApproveConfirmOpen(true);
      return;
    }
    updateBusiness(business.id, { approved: false }, {
      successMsg: 'Pulled to pending',
      errorMsg: 'Failed to update status',
    });
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    const business = approveTarget;
    setApproveConfirmOpen(false);
    setApproveTarget(null);
    await updateBusiness(business.id, { approved: true }, {
      successMsg: 'Approved',
      errorMsg: 'Failed to update status',
    });
  };

  const handleSaveEdit = async () => {
    const cleanedData = deriveServiceFields(editData);
    const ok = await updateBusiness(selectedBusiness.id, cleanedData, {
      successMsg: 'Business updated successfully',
      errorMsg: 'Failed to update business',
    });
    if (ok) setEditDialog(false);
  };
  
  const handleDeleteRequest = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    // Also close edit dialog if open
    setEditDialog(false);
    try {
      const response = await authDelete(`/admin/businesses/${id}`);
      if (response.data?.osm_warning) {
        toast.warning(response.data.osm_warning, { duration: 8000 });
      } else {
        toast.success(`"${name}" deleted`);
      }
      // Drop the deleted record locally so loaded pages stay stable.
      setBusinesses(prev => prev.filter(b => b.id !== id));
      setTotal(t => Math.max(0, (t || 0) - 1));
    } catch (error) {
      console.error('Error deleting business:', error);
      toast.error('Failed to delete business');
    }
  };
  
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, { ...getAuthConfig(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'businesses.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };
  
  const handleCategoryToggle = (category) => {
    setEditData(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category]
    }));
  };
  
  const deriveScopeLabel = (business) => {
    const scope = business.service_scope;
    if (!scope || scope === 'global') return 'Global';
    if (scope === 'region') {
      return business.service_region
        ? business.service_region.charAt(0).toUpperCase() + business.service_region.slice(1)
        : 'Region';
    }
    if (scope === 'country') {
      const country = business.service_country || business.country_code || '';
      const state = business.service_state;
      if (state && state !== 'ALL') return `${country} · ${state}`;
      if (state === 'ALL') return `${country} · National`;
      return country || 'Country';
    }
    if (scope === 'state') {
      const country = business.service_country || business.country_code || '';
      const state = business.service_state;
      if (state && state !== 'ALL') return country ? `${country} · ${state}` : state;
      if (state === 'ALL') return country ? `${country} · National` : 'National';
      return country ? `${country} · State` : 'State';
    }
    if (scope === 'county') {
      const country = business.service_country || business.country_code || '';
      const state = business.service_state;
      const county = business.service_county;
      const parts = [country, state && state !== 'ALL' ? state : null, county].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'County';
    }
    return scope;
  };

  const renderEditableCategory = (business) => {
    // If editing this business's category
    if (inlineEdit.id === business.id && inlineEdit.field === 'category') {
      return (
        <div className="flex items-center gap-1">
          <Select value={inlineEdit.value || ''} onValueChange={(v) => setInlineEdit({ ...inlineEdit, value: v })}>
            <SelectTrigger className="h-8 w-32 text-xs bg-background">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={handleInlineCategorySave} className="p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"><Check className="w-4 h-4 text-green-600" /></button>
          <button onClick={handleInlineCancel} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"><X className="w-4 h-4 text-red-600" /></button>
        </div>
      );
    }
    
    // Display mode - show category badges and make clickable
    const cats = business.category || [];
    const primaryCategory = cats[0] || '';
    
    return (
      <div 
        className="flex flex-wrap gap-1 cursor-pointer hover:bg-accent/50 px-1 py-0.5 rounded transition-colors"
        onClick={() => handleInlineEdit(business.id, 'category', primaryCategory)}
        title="Click to change category"
      >
        {cats.slice(0, 2).map((cat, idx) => (
          <span key={idx} className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{cat}</span>
        ))}
        {cats.length > 2 && (
          <span className="text-xs text-muted-foreground">+{cats.length - 2}</span>
        )}
        {cats.length === 0 && (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="admin-businesses-heading">
              OrangeLabel Businesses
            </h1>
          </div>
          <button
            onClick={handleExport}
            className="bg-primary text-white hover:bg-primary-hover rounded-md px-4 py-2 font-medium transition-all inline-flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" strokeWidth={1.5} />
            Export CSV
          </button>
        </div>
        
        {/* Community Feedback — unified queue for user-submitted flags
            (reports) and structured edit suggestions. */}
        {suggestions.length > 0 && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                Community Feedback ({suggestions.length})
              </h3>
              {(() => {
                const flagN = suggestions.filter(s => (s.kind === 'flag') || (!s.kind && !Object.keys(s.suggested_changes || {}).length)).length;
                const editN = suggestions.length - flagN;
                return (
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    {flagN} report{flagN !== 1 ? 's' : ''} · {editN} edit{editN !== 1 ? 's' : ''}
                  </span>
                );
              })()}
            </div>
            <div className="space-y-4">
              {suggestions.map(suggestion => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onApprove={handleApproveSuggestion}
                  onReject={handleRejectSuggestion}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Region filter chips */}
        <div className="mb-4 flex flex-wrap gap-1.5">
          {REGION_OPTIONS.map(opt => (
            <button
              key={opt.slug}
              onClick={() => handleRegionFilter(opt.slug)}
              className={`px-3 py-1 text-xs sm:text-sm rounded-full border transition-colors ${
                regionFilter === opt.slug
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:bg-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading businesses...</p>
          </div>
        ) : (
          <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-2">
            {(() => {
              const rows = sortedBusinesses;
              const pendingRows = rows.filter(b => !b.approved);
              const approvedRows = rows.filter(b => b.approved);
              const out = [];
              const renderCard = (b) => (
                <div key={b.id} className="bg-card border border-border rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleApproved(b)}
                      className={`mt-1.5 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${b.approved ? 'bg-green-500' : 'bg-amber-500'}`}
                      title={b.approved ? 'Approved — tap to pull to Pending' : 'Pending — tap to Approve'}
                      aria-label={b.approved ? 'Approved' : 'Pending'}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">{b.business_name}</div>
                      {b.website && (
                        <a href={b.website.startsWith('http') ? b.website : `https://${b.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all">
                          {b.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                        {getBusinessRegion(b) && (
                          <span className="bg-muted px-1.5 py-0.5 rounded">{getBusinessRegion(b)}</span>
                        )}
                        {b.country_code && (
                          <span className="bg-muted px-1.5 py-0.5 rounded">{b.country_code}</span>
                        )}
                        {deriveScopeLabel(b) && (
                          <span className="bg-muted px-1.5 py-0.5 rounded">{deriveScopeLabel(b)}</span>
                        )}
                        {(b.category || []).slice(0, 2).map((c, i) => (
                          <span key={i} className="bg-primary/10 text-primary px-1.5 py-0.5 rounded">{c}</span>
                        ))}
                        {b.created_at && (
                          <span className="bg-muted px-1.5 py-0.5 rounded" title={formatAddedFull(b.created_at)}>
                            {formatAddedAgo(b.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={() => handleEdit(b)} className="p-1.5 hover:bg-accent rounded transition-colors" title="Edit" aria-label="Edit">
                        <Pencil className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              );
              if (pendingRows.length > 0) {
                out.push(
                  <button
                    key="m-pending-hdr"
                    onClick={() => setShowPending(v => !v)}
                    className="w-full text-left bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
                  >
                    {showPending ? '▾' : '▸'} Pending review ({pendingRows.length})
                  </button>
                );
                if (showPending) pendingRows.forEach(b => out.push(renderCard(b)));
              }
              if (approvedRows.length > 0) {
                if (pendingRows.length > 0) {
                  out.push(
                    <button
                      key="m-approved-hdr"
                      onClick={() => setShowApproved(v => !v)}
                      className="w-full text-left bg-muted/60 hover:bg-muted transition-colors px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {showApproved ? '▾' : '▸'} Approved ({approvedRows.length})
                    </button>
                  );
                }
                if (showApproved) approvedRows.forEach(b => out.push(renderCard(b)));
              }
              if (out.length === 0) {
                out.push(<p key="empty" className="text-center text-sm text-muted-foreground py-8">No businesses in this region.</p>);
              }
              return out;
            })()}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="businesses-table">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground" onClick={() => handleSort('business_name')}>
                      Business {sortField === 'business_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground" onClick={() => handleSort('service_scope')}>
                      Scope {sortField === 'service_scope' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Submitter Email (private)</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">ABIB</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">NPUB</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground" onClick={() => handleSort('approved')}>
                      Status {sortField === 'approved' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground whitespace-nowrap" onClick={() => handleSort('created_at')}>
                      Added {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(() => {
                    const rows = sortedBusinesses;
                    const pendingRows = rows.filter(b => !b.approved);
                    const approvedRows = rows.filter(b => b.approved);
                    const out = [];
                    const renderRowMarker = Symbol('row-marker');
                    if (pendingRows.length > 0) {
                      out.push(
                        <tr
                          key="pending-header"
                          onClick={() => setShowPending(v => !v)}
                          className="bg-amber-50 dark:bg-amber-950/30 cursor-pointer select-none hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
                        >
                          <td colSpan="99" className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                            <span className="inline-block w-3">{showPending ? '▾' : '▸'}</span>
                            Pending review ({pendingRows.length})
                          </td>
                        </tr>
                      );
                    }
                    const pushBusinessRows = (arr) => arr.forEach(business => out.push(
                      <tr key={business.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleApproved(business)}
                            className={`mt-1 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 transition-colors hover:ring-2 hover:ring-offset-1 hover:ring-offset-background ${business.approved ? 'bg-green-500 hover:ring-green-300' : 'bg-amber-500 hover:ring-amber-300'}`}
                            title={business.approved ? 'Approved — click to pull to Pending' : 'Pending — click to Approve'}
                            aria-label={business.approved ? 'Approved — click to pull to Pending' : 'Pending — click to Approve'}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground">{business.business_name}</div>
                            {business.website && (
                              <a href={business.website.startsWith('http') ? business.website : `https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                                {business.website}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs text-muted-foreground">
                          {deriveScopeLabel(business)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {renderEditableCategory(business)}
                      </td>
                      <td className="px-3 py-2">
                        {business.submitter_email ? (
                          <span className="font-mono text-xs text-muted-foreground" title={business.submitter_email}>
                            {business.submitter_email}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {business.abib_member && (
                          <span
                            className="inline-flex items-center gap-1 text-green-600"
                            title={ABIB_TIER_SHORT[business.abib_membership] ? `ABIB ${ABIB_TIER_SHORT[business.abib_membership]}` : 'ABIB Member (no tier — not in spotlight)'}
                          >
                            ✓
                            {business.abib_membership
                              ? <span className="text-[10px] text-muted-foreground">{ABIB_TIER_SHORT[business.abib_membership]}</span>
                              : <span className="text-[10px] text-amber-500" title="No tier set">⚠</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {business.npub ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-mono text-muted-foreground truncate max-w-[80px]" title={business.npub}>
                              {business.npub.slice(0, 12)}...
                            </span>
                            <button 
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(business.npub); toast.success('Copied npub'); }}
                              className="p-1 hover:bg-accent rounded"
                              title="Copy npub"
                            >
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            </button>
                            <a 
                              href={`https://njump.me/${business.npub}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-accent rounded"
                              title="View on njump"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3 text-muted-foreground" />
                            </a>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${business.approved ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>
                          {business.approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-xs text-muted-foreground" title={formatAddedFull(business.created_at)}>
                          {formatAddedAgo(business.created_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => handleEdit(business)} className="p-1.5 hover:bg-accent rounded transition-colors" title="Edit">
                            <Pencil className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                      ));
                    if (showPending) pushBusinessRows(pendingRows);
                    if (approvedRows.length > 0) {
                      if (pendingRows.length > 0) {
                        out.push(
                          <tr
                            key="approved-header"
                            onClick={() => setShowApproved(v => !v)}
                            className="bg-muted/60 cursor-pointer select-none hover:bg-muted transition-colors"
                          >
                            <td colSpan="99" className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                              <span className="inline-block w-3">{showApproved ? '▾' : '▸'}</span>
                              Approved ({approvedRows.length})
                            </td>
                          </tr>
                        );
                      }
                      if (showApproved) pushBusinessRows(approvedRows);
                    }
                    // touch renderRowMarker to quiet unused-var lints
                    void renderRowMarker;
                    return out;
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination footer — surfaces the full record count and lets the
              admin pull pages beyond the first 500 so nothing is hidden. */}
          <div className="mt-4 flex items-center justify-center gap-3 text-sm text-muted-foreground" data-testid="businesses-pagination">
            <span>
              {regionFilter === 'all'
                ? `Showing ${businesses.length}${total ? ` of ${total}` : ''}`
                : `Showing ${sortedBusinesses.length} filtered${total ? ` (of ${total} total)` : ''}`}
            </span>
            {regionFilter === 'all' && nextOffset != null && (
              <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loadingMore} data-testid="load-more-btn">
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
          </>
        )}
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Business</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4" data-testid="edit-business-body">

            {/* ── Identity ───────────────────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-foreground">Identity</legend>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input value={editData.business_name || ''} onChange={(e) => setEditData({ ...editData, business_name: e.target.value })} data-testid="edit-business-name" />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input value={editData.website || ''} onChange={(e) => setEditData({ ...editData, website: e.target.value })} type="url" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Public Email</Label>
                  <Input
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    placeholder="hello@business.com"
                    type="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact / Phone</Label>
                  <Input value={editData.contact || ''} onChange={(e) => setEditData({ ...editData, contact: e.target.value })} className="bg-background" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categories</Label>
                <div className="grid grid-cols-2 gap-2 border border-border rounded-md p-3 max-h-40 overflow-y-auto">
                  {categories.map((cat) => (
                    <div key={cat} className="flex items-center space-x-2">
                      <Checkbox checked={(editData.category || []).includes(cat)} onCheckedChange={() => handleCategoryToggle(cat)} />
                      <label className="text-sm cursor-pointer">{cat}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={!!editData.abib_member}
                      onCheckedChange={(c) => setEditData({ ...editData, abib_member: c, abib_membership: c ? editData.abib_membership : '' })}
                    />
                    <label className="text-sm">ABIB Member</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={!!editData.bitcoin_service} onCheckedChange={(c) => setEditData({ ...editData, bitcoin_service: c })} />
                    <label className="text-sm">Bitcoin Service</label>
                  </div>
                </div>
                {editData.abib_member && (
                  <div className="space-y-1">
                    <Label className="text-sm">Membership tier</Label>
                    <Select
                      value={editData.abib_membership || 'none'}
                      onValueChange={(v) => setEditData({ ...editData, abib_membership: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="w-72 bg-background" data-testid="abib-tier-select">
                        <SelectValue placeholder="Select tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None (not in spotlight)</SelectItem>
                        {ABIB_TIER_OPTIONS.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Only tiered members appear in the ABIB Member Spotlight social posts.
                    </p>
                  </div>
                )}
              </div>
            </fieldset>

            {/* ── Based Location ──────────────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-3" data-testid="based-location-fieldset">
              <legend className="px-1 text-sm font-medium text-foreground">Based Location</legend>
              <div className="flex items-center space-x-2">
                <Checkbox checked={!!editData.has_physical_location} onCheckedChange={(c) => setEditData({ ...editData, has_physical_location: !!c })} />
                <label className="text-sm">Has a physical location (shown on /map and submitted to OSM when approved)</label>
              </div>

              {/* Country → State → County cascading pickers */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select
                    value={editData.country_code || ''}
                    onValueChange={async (v) => {
                      // Reset state + county when country changes
                      setEditData(prev => ({ ...prev, country_code: v, based_state: '', based_county: '' }));
                      const states = await fetchCountryStates(v);
                      setBasedCountryStates(states);
                    }}
                    data-testid="based-country-select"
                  >
                    <SelectTrigger className="bg-background" data-testid="based-country-trigger">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 overflow-y-auto">
                      {ALL_COUNTRY_OPTIONS.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Based State / Province</Label>
                  {basedCountryStates.length > 0 ? (
                    <Select
                      value={editData.based_state || ''}
                      onValueChange={(v) => setEditData(prev => ({ ...prev, based_state: v }))}
                      data-testid="based-state-select"
                    >
                      <SelectTrigger className="bg-background" data-testid="based-state-trigger">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64 overflow-y-auto">
                        {basedCountryStates.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={editData.based_state || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, based_state: e.target.value }))}
                      placeholder={editData.country_code ? 'State / province' : 'Select country first'}
                      disabled={!editData.country_code}
                      data-testid="based-state-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Based County / Suburb</Label>
                  <Input
                    value={editData.based_county || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, based_county: e.target.value }))}
                    placeholder="County, suburb, district…"
                    data-testid="based-county-input"
                  />
                </div>
              </div>
              {/* Legacy region field — kept for backward compat, shown as read-only hint */}
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Region (legacy — kept for backward compat)</Label>
                <Input
                  value={editData.region || ''}
                  onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                  placeholder="NSW, global, …"
                  className="text-xs text-muted-foreground h-8"
                />
              </div>

              {/* Physical location address + coords block */}
              {editData.has_physical_location && (
                <>
                  <div className="grid grid-cols-6 gap-3">
                    <div className="col-span-1 space-y-2">
                      <Label>House #</Label>
                      <Input value={editData.addr_housenumber || ''} onChange={(e) => setEditData({ ...editData, addr_housenumber: e.target.value })} />
                    </div>
                    <div className="col-span-3 space-y-2">
                      <Label>Street</Label>
                      <Input value={editData.addr_street || ''} onChange={(e) => setEditData({ ...editData, addr_street: e.target.value })} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Suburb</Label>
                      <Input value={editData.addr_suburb || ''} onChange={(e) => setEditData({ ...editData, addr_suburb: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label>City</Label>
                      <Input value={editData.addr_city || ''} onChange={(e) => setEditData({ ...editData, addr_city: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>State (address)</Label>
                      <Input value={editData.addr_state || ''} onChange={(e) => setEditData({ ...editData, addr_state: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode</Label>
                      <Input value={editData.addr_postcode || ''} onChange={(e) => setEditData({ ...editData, addr_postcode: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.latitude ?? ''}
                        onChange={(e) => setEditData({ ...editData, latitude: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.longitude ?? ''}
                        onChange={(e) => setEditData({ ...editData, longitude: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </>
              )}
            </fieldset>

            {/* ── Service Area ─────────────────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-4" data-testid="service-area-fieldset">
              <legend className="px-1 text-sm font-medium text-foreground">Service Area</legend>

              {/* Scope radio group */}
              <div className="space-y-2">
                <Label>Scope</Label>
                <div className="flex flex-wrap gap-4" data-testid="service-scope-radios">
                  {['global', 'region', 'country', 'state', 'county'].map(scope => (
                    <label key={scope} className="flex items-center gap-1.5 cursor-pointer text-sm capitalize">
                      <input
                        type="radio"
                        name="service_scope"
                        value={scope}
                        checked={editData.service_scope === scope}
                        onChange={() => setEditData(prev => ({ ...prev, service_scope: scope }))}
                        data-testid={`scope-radio-${scope}`}
                      />
                      {scope.charAt(0).toUpperCase() + scope.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Tier 1: Region (visible when scope ∈ {region, country, state, county}) */}
              {['region', 'country', 'state', 'county'].includes(editData.service_scope) && (
                <div className="space-y-2 pl-4 border-l-2 border-border">
                  <Label>UN Region</Label>
                  <Select
                    value={editData.service_region || ''}
                    onValueChange={(v) => {
                      // Reset country/state/county when region changes
                      setEditData(prev => ({
                        ...prev,
                        service_region: v,
                        service_country: '',
                        service_state: '',
                        service_county: '',
                      }));
                      setServiceCountryStates([]);
                    }}
                    data-testid="service-region-select"
                  >
                    <SelectTrigger className="bg-background max-w-xs" data-testid="service-region-trigger">
                      <SelectValue placeholder="Select UN region" />
                    </SelectTrigger>
                    <SelectContent>
                      {UN_REGIONS.map(r => (
                        <SelectItem key={r.slug} value={r.slug}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Tier 2: Country (visible when scope ∈ {country, state, county}) */}
              {['country', 'state', 'county'].includes(editData.service_scope) && (
                <div className="space-y-2 pl-8 border-l-2 border-border">
                  <Label>Country</Label>
                  <Select
                    value={editData.service_country || ''}
                    onValueChange={async (v) => {
                      setEditData(prev => ({ ...prev, service_country: v, service_state: '', service_county: '' }));
                      const states = await fetchCountryStates(v);
                      setServiceCountryStates(states);
                    }}
                    data-testid="service-country-select"
                  >
                    <SelectTrigger className="bg-background max-w-xs" data-testid="service-country-trigger">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 overflow-y-auto">
                      {getCountriesForRegion(editData.service_region).map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Tier 3: State (visible when scope ∈ {state, county}) */}
              {['state', 'county'].includes(editData.service_scope) && (
                <div className="space-y-2 pl-12 border-l-2 border-border">
                  <Label>State / Province</Label>
                  {serviceCountryStates.length > 0 ? (
                    <Select
                      value={editData.service_state || ''}
                      onValueChange={(v) => setEditData(prev => ({ ...prev, service_state: v, service_county: '' }))}
                      data-testid="service-state-select"
                    >
                      <SelectTrigger className="bg-background max-w-xs" data-testid="service-state-trigger">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64 overflow-y-auto">
                        <SelectItem value="ALL">All states (national)</SelectItem>
                        {serviceCountryStates.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={editData.service_state || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, service_state: e.target.value }))}
                      placeholder={editData.service_country ? 'State code or ALL' : 'Select country first'}
                      disabled={!editData.service_country}
                      className="max-w-xs"
                      data-testid="service-state-input"
                    />
                  )}
                </div>
              )}

              {/* Tier 4: County (visible when scope = county) */}
              {editData.service_scope === 'county' && (
                <div className="space-y-2 pl-16 border-l-2 border-border">
                  <Label>County / District / Suburb</Label>
                  <Input
                    value={editData.service_county || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, service_county: e.target.value }))}
                    placeholder="County, district, suburb…"
                    className="max-w-xs"
                    data-testid="service-county-input"
                  />
                </div>
              )}

              {editData.btcmap_already_listed && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md p-3 text-sm text-amber-800 dark:text-amber-200">
                  Merchant is already on BTCMap (id: {editData.btcmap_id || editData.btc_map_id || 'unknown'}) — only the OrangeLabel record will be updated on save.
                </div>
              )}
            </fieldset>

            {/* ── Payment ────────────────────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-foreground">Bitcoin payment methods</legend>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox checked={!!editData.payment_onchain} onCheckedChange={(c) => setEditData({ ...editData, payment_onchain: !!c })} />
                  <label className="text-sm">On-chain</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox checked={!!editData.payment_lightning} onCheckedChange={(c) => setEditData({ ...editData, payment_lightning: !!c })} />
                  <label className="text-sm">Lightning</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox checked={!!editData.payment_lightning_contactless} onCheckedChange={(c) => setEditData({ ...editData, payment_lightning_contactless: !!c })} />
                  <label className="text-sm">Lightning NFC / Contactless</label>
                </div>
              </div>
            </fieldset>

            {/* ── Sync status (read-only) ────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-2 text-sm">
              <legend className="px-1 text-sm font-medium text-foreground">Sync status (read-only)</legend>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs">
                <div><span className="text-muted-foreground">source:</span> {editData.source || '—'}</div>
                <div><span className="text-muted-foreground">btc_map:</span> {editData.btc_map ? 'true' : 'false'}</div>
                <div><span className="text-muted-foreground">btc_map_id:</span> {editData.btc_map_id || '—'}</div>
                <div><span className="text-muted-foreground">btc_map_pending:</span> {editData.btc_map_pending ? 'true' : 'false'}</div>
                <div className="col-span-2"><span className="text-muted-foreground">btc_map_url:</span>{' '}
                  {editData.btc_map_url
                    ? <a href={editData.btc_map_url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{editData.btc_map_url}</a>
                    : '—'}
                </div>
                <div><span className="text-muted-foreground">osm_node_id:</span>{' '}
                  {editData.osm_node_id
                    ? <a href={`https://www.openstreetmap.org/node/${editData.osm_node_id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{String(editData.osm_node_id)}</a>
                    : '—'}
                </div>
                <div><span className="text-muted-foreground">osm_changeset_id:</span>{' '}
                  {editData.osm_changeset_id
                    ? <a href={`https://www.openstreetmap.org/changeset/${editData.osm_changeset_id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{String(editData.osm_changeset_id)}</a>
                    : '—'}
                </div>
                <div><span className="text-muted-foreground">osm_action:</span> {editData.osm_action || '—'}</div>
                <div><span className="text-muted-foreground">osm_submitted_at:</span> {editData.osm_submitted_at || '—'}</div>
                <div><span className="text-muted-foreground">updated_at:</span> {editData.updated_at || '—'}</div>
                <div><span className="text-muted-foreground">approved:</span> {editData.approved ? 'true' : 'false'}{' '}
                  <span className="text-[10px] text-muted-foreground">(toggle via the status dot on the list)</span>
                </div>
              </div>
            </fieldset>

            {/* ── Admin notes ────────────────────────────── */}
            <fieldset className="border border-border rounded-md p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-foreground">Admin notes</legend>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={editData.notes || ''} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>NOSTR Public Key (npub)</Label>
                <Input
                  value={editData.npub || ''}
                  onChange={(e) => setEditData({ ...editData, npub: e.target.value })}
                  placeholder="npub1..."
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Submitter Email <span className="text-muted-foreground font-normal">(private — not shown publicly, not sent to OSM)</span></Label>
                <Input
                  value={editData.submitter_email || ''}
                  onChange={(e) => setEditData({ ...editData, submitter_email: e.target.value })}
                  placeholder="submitter@example.com"
                  className="font-mono text-sm"
                  type="email"
                />
              </div>
            </fieldset>

            <div className="flex justify-between items-center gap-2 pt-2">
              <button
                onClick={() => handleDeleteRequest(selectedBusiness?.id, selectedBusiness?.business_name)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                Delete Business
              </button>
              <div className="flex gap-2">
                <button onClick={() => setEditDialog(false)} className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">Cancel</button>
                <button onClick={handleSaveEdit} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors" data-testid="save-business-btn">Save Changes</button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => { if (!open) { setDeleteConfirmOpen(false); setDeleteTarget(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
              Delete Business
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-foreground">
              Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              If this business has an OSM node, it will also be removed from OpenStreetMap.
              BTCMap will reconcile automatically within ~10 minutes.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Modal — the false→true transition fires the
          irreversible OSM node-create pipeline server-side, so confirm first. */}
      <Dialog open={approveConfirmOpen} onOpenChange={(open) => { if (!open) { setApproveConfirmOpen(false); setApproveTarget(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
              Approve Business
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-foreground">
              Approve <strong>{approveTarget?.business_name}</strong>?
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Approving may submit this business to OpenStreetMap (for physical
              locations with coordinates that accept Bitcoin payment — a new OSM
              node is created in that case). Any such submission runs in the
              background and cannot be undone by simply pulling the record back
              to pending.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setApproveConfirmOpen(false); setApproveTarget(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Approve
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBusinesses;
